import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useAlertDialog } from '@goodfynd/ui';

export type LocationPermissionStatus =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'unsupported';

interface UseLocationPermissionOptions {
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
  customMessages?: {
    denied?: ReactNode;
    unsupported?: ReactNode;
    prompt?: ReactNode;
  };
}

interface UseLocationPermissionReturn {
  checkAndRequestPermission: () => Promise<boolean>;
  hasPermission: boolean;
  permissionStatus: LocationPermissionStatus;
  requestLocation: (options?: PositionOptions) => Promise<GeolocationPosition>;
}

export function useLocationPermission(
  options: UseLocationPermissionOptions = {}
): UseLocationPermissionReturn {
  const { onPermissionGranted, onPermissionDenied, customMessages } = options;
  const openAlert = useAlertDialog();
  const [permissionStatus, setPermissionStatus] =
    useState<LocationPermissionStatus>('prompt');
  const [hasPermission, setHasPermission] = useState(false);

  const checkPermissionStatus =
    useCallback(async (): Promise<LocationPermissionStatus> => {
      if (!navigator.geolocation) {
        return 'unsupported';
      }

      // Check if Permissions API is available
      if ('permissions' in navigator && 'query' in navigator.permissions) {
        try {
          const permission = await navigator.permissions.query({
            name: 'geolocation',
          });
          const status = permission.state;
          if (status === 'granted') {
            return 'granted';
          }
          if (status === 'denied') {
            return 'denied';
          }
          return 'prompt';
        } catch (error) {
          // Permissions API might not support geolocation on all browsers
          console.warn('Error checking geolocation permission:', error);
          return 'prompt';
        }
      }

      // Fallback: assume prompt if Permissions API not available
      return 'prompt';
    }, []);

  const checkAndRequestPermission = useCallback(async (): Promise<boolean> => {
    const status = await checkPermissionStatus();
    setPermissionStatus(status);

    if (status === 'granted') {
      setHasPermission(true);
      onPermissionGranted?.();
      return true;
    }

    if (status === 'denied') {
      setHasPermission(false);
      if (customMessages?.denied !== null) {
        openAlert({
          title: 'Location Permission Denied',
          message:
            customMessages?.denied ||
            'Location permission has been denied. To use this feature, please enable location access in your browser settings.',
        });
      }
      onPermissionDenied?.();
      return false;
    }

    if (status === 'unsupported') {
      if (customMessages?.unsupported !== null) {
        openAlert({
          title: 'Location Not Supported',
          message:
            customMessages?.unsupported ||
            'Geolocation is not supported by your browser. Please use a different browser.',
        });
      }
      onPermissionDenied?.();
      return false;
    }

    // status === 'prompt' - show our informative alert before requesting
    // The browser permission prompt will appear when requestLocation is called
    if (customMessages?.prompt !== null) {
      openAlert({
        title: 'Location Permission Required',
        message:
          customMessages?.prompt ||
          'To use this feature, we need access to your location. Please grant location permission when prompted.',
      });
    }

    // Permission will be requested when requestLocation is called
    setPermissionStatus('prompt');
    return true;
  }, [
    checkPermissionStatus,
    customMessages?.denied,
    customMessages?.prompt,
    customMessages?.unsupported,
    onPermissionDenied,
    onPermissionGranted,
    openAlert,
  ]);

  const requestLocation = useCallback(
    (positionOptions: PositionOptions = {}): Promise<GeolocationPosition> => {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'));
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            setPermissionStatus('granted');
            setHasPermission(true);
            onPermissionGranted?.();
            resolve(position);
          },
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              setPermissionStatus('denied');
              setHasPermission(false);
              onPermissionDenied?.();
              reject(new Error('Location permission denied'));
            } else {
              reject(error);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
            ...positionOptions,
          }
        );
      });
    },
    [onPermissionGranted, onPermissionDenied]
  );

  return {
    checkAndRequestPermission,
    hasPermission,
    permissionStatus,
    requestLocation,
  };
}
